import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import DataGrid, { Column, EditorProps, FormatterProps } from 'react-data-grid';

import { areEqual } from 'Utils/equalityChecks';
import { Api } from 'Utils/api';

import classes from './referralFees.module.css';

// Custom currency editor that only accepts numeric input
function CurrencyEditor<TRow extends { feeLost: number; feeWon: number }, TSummaryRow>({
  row,
  column,
  onRowChange,
  onClose,
}: EditorProps<TRow, TSummaryRow>) {
  const value = row[column.key as keyof TRow] as number;
  const [inputValue, setInputValue] = useState(value === 0 ? '' : String(value));

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    // Only allow digits, decimal point, and empty string
    if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) {
      setInputValue(val);
    }
  };

  const handleBlur = () => {
    const numValue = parseFloat(inputValue) || 0;
    onRowChange({ ...row, [column.key]: numValue }, true);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const numValue = parseFloat(inputValue) || 0;
      onRowChange({ ...row, [column.key]: numValue }, true);
      onClose();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      className={classes.currencyInput}
      value={inputValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      autoFocus
    />
  );
}

interface ReferralFeeRow {
  id: string; // opportunity ID
  feeId: string | null; // referral fee record ID (null if not yet created)
  opportunityName: string;
  referralPartner: string;
  referredBy: string;
  contactName: string;
  status: string;
  dateCreated: string;
  monetaryValue: number;
  feeLost: number;
  lostPaid: boolean;
  feeWon: number;
  wonPaid: boolean;
}

interface ReferralFeesProps {
  locationId: string;
  ssoToken: string;
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

function ReferralFeesComponent({
  locationId,
  ssoToken,
}: ReferralFeesProps) {
  const [rows, setRows] = useState<ReferralFeeRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [savingRows, setSavingRows] = useState<Set<string>>(new Set());

  const loadReferrals = useCallback(async () => {
    if (!locationId || !ssoToken) {
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Load all opportunities with referrals (fee data is eagerly loaded via referral_fee relationship)
      const opportunitiesResponse = await Api.get('/ghl/embed/opportunities', {
        params: {
          locationId,
          with_referral: true,
        },
        headers: { 'X-GHL-SSO': ssoToken },
      });

      // Parse opportunities
      const opportunities = Array.isArray(opportunitiesResponse?.data?.opportunities)
        ? opportunitiesResponse.data.opportunities
        : Array.isArray(opportunitiesResponse?.data?.data)
          ? opportunitiesResponse.data.data
          : Array.isArray(opportunitiesResponse?.data)
            ? opportunitiesResponse.data
            : [];

      // Map opportunities to display format with merged fee data
      const mappedRows: ReferralFeeRow[] = opportunities.map((opp: any) => {
        const contact = opp.contact || {};
        const firstName = contact.first_name || contact.firstName || contact.firstNameLowerCase || '';
        const lastName = contact.last_name || contact.lastName || contact.lastNameLowerCase || '';
        const contactName = [firstName, lastName].filter((n) => n.trim()).join(' ').trim() || '(No Name)';

        // Get referral partner info - check opportunity, fee relationship, and contact
        const referredByData = opp.referred_by || opp.referral_fee?.referral_partner || contact.referred_by || {};
        const refFirstName = referredByData.first_name || referredByData.firstName || '';
        const refLastName = referredByData.last_name || referredByData.lastName || '';
        const referredBy = [refFirstName, refLastName].filter((n) => n.trim()).join(' ').trim() || '(Unknown)';
        const referralPartner = referredByData.company_name || referredByData.companyName || '';

        // Get fee data from eagerly loaded referral_fee relationship
        const fee = opp.referral_fee || {};

        return {
          id: opp.id,
          feeId: fee.id || null,
          opportunityName: opp.name || '(No Name)',
          referralPartner,
          referredBy,
          contactName,
          status: opp.status || 'unknown',
          dateCreated: opp.created_at || opp.createdAt || opp.dateAdded || '',
          monetaryValue: opp.monetary_value || opp.monetaryValue || 0,
          feeLost: fee.lost_fee ?? 0,
          lostPaid: fee.lost_fee_paid ?? false,
          feeWon: fee.won_fee ?? 0,
          wonPaid: fee.won_fee_paid ?? false,
        };
      });

      // Sort by date descending
      mappedRows.sort((a, b) => new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime());

      setRows(mappedRows);
    } catch (err: any) {
      // eslint-disable-next-line no-console
      console.error('Error loading referrals:', err);
      setError('Failed to load referrals. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [locationId, ssoToken]);

  useEffect(() => {
    loadReferrals();
  }, [loadReferrals]);

  // Handle row changes (for editable cells)
  const handleRowsChange = useCallback(async (newRows: ReferralFeeRow[], { indexes }: { indexes: number[] }) => {
    setRows(newRows);

    // Update each changed row on the backend
    for (const index of indexes) {
      const row = newRows[index];
      setSavingRows((prev) => new Set(prev).add(row.id));

      try {
        if (row.feeId) {
          // Update existing fee record
          await Api.put(`/ghl/embed/referral-fees/${row.feeId}`, {
            lost_fee: row.feeLost,
            won_fee: row.feeWon,
            lost_fee_paid: row.lostPaid,
            won_fee_paid: row.wonPaid,
          }, {
            headers: { 'X-GHL-SSO': ssoToken },
          });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to save referral fee:', err);
      } finally {
        setSavingRows((prev) => {
          const next = new Set(prev);
          next.delete(row.id);
          return next;
        });
      }
    }
  }, [ssoToken]);

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return '-';
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status.toLowerCase()) {
      case 'open':
        return 'bg-success';
      case 'won':
        return 'bg-primary';
      case 'lost':
        return 'bg-danger';
      case 'abandoned':
        return 'bg-secondary';
      default:
        return 'bg-secondary';
    }
  };

  // Paid toggle formatter - toggles are mutually exclusive per row
  const PaidToggleFormatter = useCallback(({ row, column, onRowChange }: FormatterProps<ReferralFeeRow>) => {
    const isLost = column.key === 'lostPaid';
    const checked = isLost ? row.lostPaid : row.wonPaid;

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (isLost) {
        onRowChange({ ...row, lostPaid: e.target.checked, wonPaid: e.target.checked ? false : row.wonPaid });
      } else {
        onRowChange({ ...row, wonPaid: e.target.checked, lostPaid: e.target.checked ? false : row.lostPaid });
      }
    };

    return (
      <div className="form-check form-switch d-flex align-items-center justify-content-center m-0" style={{ height: '100%' }}>
        <input
          type="checkbox"
          className="form-check-input"
          role="switch"
          checked={checked}
          onChange={handleChange}
          disabled={!row.feeId || savingRows.has(row.id)}
        />
      </div>
    );
  }, [savingRows]);

  const columns: Column<ReferralFeeRow>[] = useMemo(() => [
    {
      key: 'opportunityName',
      name: 'Opportunity',
      width: 180,
      resizable: true,
    },
    {
      key: 'contactName',
      name: 'Contact',
      width: 150,
      resizable: true,
    },
    {
      key: 'referralPartner',
      name: 'Referral Partner',
      width: 150,
      resizable: true,
      formatter: ({ row }: FormatterProps<ReferralFeeRow>) => (
        <div>
          {row.referralPartner || row.referredBy}
          {row.referralPartner && row.referredBy && (
            <small className="d-block text-muted">{row.referredBy}</small>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      name: 'Status',
      width: 100,
      resizable: true,
      formatter: ({ row }: FormatterProps<ReferralFeeRow>) => (
        <span className={`badge ${getStatusBadgeClass(row.status)}`}>
          {row.status.toUpperCase()}
        </span>
      ),
    },
    {
      key: 'monetaryValue',
      name: 'Value',
      width: 110,
      resizable: true,
      formatter: ({ row }: FormatterProps<ReferralFeeRow>) => <>{currencyFormatter.format(row.monetaryValue)}</>,
    },
    {
      key: 'dateCreated',
      name: 'Date',
      width: 100,
      resizable: true,
      formatter: ({ row }: FormatterProps<ReferralFeeRow>) => <>{formatDate(row.dateCreated)}</>,
    },
    {
      key: 'feeLost',
      name: 'Lost Fee',
      width: 110,
      resizable: true,
      editor: CurrencyEditor,
      editable: (row) => !!row.feeId,
      editorOptions: { editOnClick: true },
      formatter: ({ row }: FormatterProps<ReferralFeeRow>) => (
        <span className={savingRows.has(row.id) ? 'text-muted' : ''}>
          {currencyFormatter.format(row.feeLost)}
        </span>
      ),
    },
    {
      key: 'lostPaid',
      name: 'Paid',
      width: 60,
      resizable: true,
      formatter: PaidToggleFormatter,
    },
    {
      key: 'feeWon',
      name: 'Won Fee',
      width: 110,
      resizable: true,
      editor: CurrencyEditor,
      editable: (row) => !!row.feeId,
      editorOptions: { editOnClick: true },
      formatter: ({ row }: FormatterProps<ReferralFeeRow>) => (
        <span className={savingRows.has(row.id) ? 'text-muted' : ''}>
          {currencyFormatter.format(row.feeWon)}
        </span>
      ),
    },
    {
      key: 'wonPaid',
      name: 'Paid',
      width: 60,
      resizable: true,
      formatter: PaidToggleFormatter,
    },
    {
      key: 'total',
      name: 'Total Fee',
      width: 110,
      resizable: true,
      formatter: ({ row }: FormatterProps<ReferralFeeRow>) => <>{currencyFormatter.format(row.feeLost + row.feeWon)}</>,
    },
  ], [savingRows, PaidToggleFormatter]);

  // Filter rows by search term
  const filteredRows = useMemo(() => {
    if (!searchTerm) return rows;
    const searchLower = searchTerm.toLowerCase();
    return rows.filter(
      (row) =>
        row.opportunityName.toLowerCase().includes(searchLower)
        || row.contactName.toLowerCase().includes(searchLower)
        || row.referralPartner.toLowerCase().includes(searchLower)
        || row.referredBy.toLowerCase().includes(searchLower)
    );
  }, [rows, searchTerm]);

  // Calculate totals
  const totals = useMemo(() => filteredRows.reduce(
      (acc, row) => ({
        feeLost: acc.feeLost + row.feeLost,
        feeWon: acc.feeWon + row.feeWon,
        total: acc.total + row.feeLost + row.feeWon,
      }),
      { feeLost: 0, feeWon: 0, total: 0 }
    ), [filteredRows]);

  if (isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">Loading referrals...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <h5 className="mb-3">Referral Fees</h5>

      {/* Search and Summary */}
      <div className="row mb-3 align-items-end">
        <div className="col-md-4 mb-2">
          <input
            type="text"
            className="form-control"
            placeholder="Search by name, partner, or contact..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="col-md-8 mb-2">
          <span className="me-4">
            <strong>Lost Fees:</strong>
{' '}
{currencyFormatter.format(totals.feeLost)}
          </span>
          <span className="me-4">
            <strong>Won Fees:</strong>
{' '}
{currencyFormatter.format(totals.feeWon)}
          </span>
          <span>
            <strong>Total:</strong>
{' '}
{currencyFormatter.format(totals.total)}
          </span>
        </div>
      </div>

      <p className="text-muted mb-2">
        <small>Click on fee cells to edit. Changes are saved automatically.</small>
      </p>

      {filteredRows.length === 0 ? (
        <div className="alert alert-info">
          {searchTerm
            ? 'No referrals found matching your search.'
            : 'No referrals found.'}
        </div>
      ) : (
        <DataGrid
          className="rdg-light"
          columns={columns}
          rows={filteredRows}
          onRowsChange={handleRowsChange}
          rowKeyGetter={(row) => row.id}
          style={{ height: 'calc(100vh - 320px)', minHeight: '400px' }}
        />
      )}
    </div>
  );
}

const ReferralFeesMemo = memo(ReferralFeesComponent, areEqual);

export { ReferralFeesMemo as ReferralFees };
